/**
 * Hook for handling dual file upload in DataCloud
 * Manages file1 (predictors) and file2 (target variable) upload and parsing
 *
 * File-validation errors are dispatched directly to the UI reducer (SET_ERROR)
 * so the ErrorMessage toast appears without an intermediary bridge effect.
 * This matches the pattern used by useDataCloudRegression and
 * useDataCloudInterpolation.
 */
import { useState, useRef, useCallback, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataPoint,
  InterpolatedPoint,
  DataCloudUIAction,
  CloudErrorPayload,
  DEFAULT_X_LABEL,
  DEFAULT_Y_LABEL
} from '../types/dataCloud.types';

interface ParsedFileData {
  data: InterpolatedPoint[];
  header: string;
}

interface UseDataCloudFileProps {
  dispatch: Dispatch<DataCloudUIAction>;
}

interface UseDataCloudFileReturn {
  // File states
  file1: File | null;
  file2: File | null;

  // Parsed data
  rawData: DataPoint[];
  xAxisLabel: string;
  yAxisLabel: string;

  // Handlers
  handleFileUpload: (fileNumber: 1 | 2) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  resetFiles: () => void;
  removeFile1: () => void;
  removeFile2: () => void;

  // Status
  isFileReady: boolean;
  fileError: string | null;
  clearError: () => void;
}

export const useDataCloudFile = ({ dispatch }: UseDataCloudFileProps): UseDataCloudFileReturn => {
  const { t } = useTranslation('dataCloud');

  // File states
  const [file1, setFile1] = useState<File | null>(null);
  const [file2, setFile2] = useState<File | null>(null);

  // Data states
  const [rawData, setRawData] = useState<DataPoint[]>([]);
  const [xAxisLabel, setXAxisLabel] = useState<string>(DEFAULT_X_LABEL);
  const [yAxisLabel, setYAxisLabel] = useState<string>(DEFAULT_Y_LABEL);

  // Error state — kept for backward-compat (isFileReady, clearError, fileError
  // are still used by callers) but now also dispatches SET_ERROR directly.
  const [fileError, setFileError] = useState<string | null>(null);

  // Temp storage for file data before merge
  const tempFile1Data = useRef<ParsedFileData | null>(null);
  const tempFile2Data = useRef<ParsedFileData | null>(null);

  /**
   * Set a file-validation error both locally and on the UI reducer so the
   * ErrorMessage toast renders immediately.
   */
  const raiseFileError = useCallback(
    (message: string) => {
      setFileError(message);
      const payload: CloudErrorPayload = {
        code: undefined,
        suggestion: undefined,
        fallback: message,
      };
      dispatch({ type: 'SET_ERROR', payload });
    },
    [dispatch],
  );

  /**
   * Parse CSV file content
   */
  const parseFileContent = useCallback(
    (text: string): ParsedFileData | null => {
      if (!text) {
        raiseFileError(t('errors.fileIsEmpty'));
        return null;
      }

      const lines = text.split(/\r?\n/).filter(Boolean);

      // Validation: Minimum 2 lines (header + at least 1 data row)
      if (lines.length < 2) {
        raiseFileError(t('errors.notEnoughDataRows'));
        return null;
      }

      // Auto-detect separator
      let sep = ',';
      if (lines[0].includes(';')) sep = ';';
      else if (lines[0].includes('\t')) sep = '\t';

      const headers = lines[0].split(sep).map(h => h.trim());

      // Validation: Must have exactly 2 columns (UTC + value)
      if (headers.length !== 2) {
        raiseFileError(
          t('errors.invalidColumnCount', { count: headers.length })
        );
        return null;
      }

      // Validation: UTC column must be present
      const utcIndex = headers.findIndex(h => h.toLowerCase() === 'utc');
      if (utcIndex === -1) {
        raiseFileError(t('errors.missingUtcColumn'));
        return null;
      }

      const valueIndex = utcIndex === 0 ? 1 : 0;

      // Parse data rows
      const parsedData: InterpolatedPoint[] = lines
        .slice(1)
        .map(line => {
          const parts = line.split(sep);
          return {
            UTC: parts[utcIndex]?.trim() || '',
            value: parseFloat(parts[valueIndex]?.replace(',', '.') || '')
          };
        })
        .filter(d => d.UTC && !isNaN(d.value));

      // Validation: Must have at least 1 valid data row
      if (parsedData.length === 0) {
        raiseFileError(t('errors.noValidDataRows'));
        return null;
      }

      return {
        data: parsedData,
        header: headers[valueIndex]
      };
    },
    [t, raiseFileError]
  );

  /**
   * Merge data from both files by UTC timestamp
   */
  const mergeFileData = useCallback(() => {
    if (!tempFile1Data.current?.data.length || !tempFile2Data.current?.data.length) {
      return;
    }

    // Create map of file2 data by UTC
    const dataMap2 = new Map(
      tempFile2Data.current.data.map(d => [String(d.UTC), d.value])
    );

    // Combine data where UTC timestamps match
    const combinedData: DataPoint[] = tempFile1Data.current.data
      .map(d => {
        const match = dataMap2.get(String(d.UTC));
        return match !== undefined
          ? { x: Number(d.value), y: Number(match) }
          : null;
      })
      .filter((d): d is DataPoint => d !== null);

    setRawData(combinedData);
  }, []);

  /**
   * Handle file upload for either file1 or file2
   */
  const handleFileUpload = useCallback(
    (fileNumber: 1 | 2) =>
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || !event.target.files[0]) {
          raiseFileError(t('errors.noFileSelected'));
          return;
        }

        const file = event.target.files[0];
        setFileError(null);

        const reader = new FileReader();

        reader.onload = async e => {
          const text = e.target?.result as string;
          const parsed = parseFileContent(text);

          if (!parsed) return;

          if (fileNumber === 1) {
            setFile1(file);
            setXAxisLabel(parsed.header);
            tempFile1Data.current = parsed;
          } else {
            setFile2(file);
            setYAxisLabel(parsed.header);
            tempFile2Data.current = parsed;
          }

          // Try to merge if both files are ready
          mergeFileData();
        };

        reader.onerror = () => {
          raiseFileError(t('errors.fileReadError'));
        };

        reader.readAsText(file);
      },
    [t, raiseFileError, parseFileContent, mergeFileData]
  );

  /**
   * Remove file1 (predictors) and clear its derived state
   */
  const removeFile1 = useCallback(() => {
    setFile1(null);
    setXAxisLabel(DEFAULT_X_LABEL);
    setRawData([]);
    tempFile1Data.current = null;
  }, []);

  /**
   * Remove file2 (target variable) and clear its derived state
   */
  const removeFile2 = useCallback(() => {
    setFile2(null);
    setYAxisLabel(DEFAULT_Y_LABEL);
    setRawData([]);
    tempFile2Data.current = null;
  }, []);

  /**
   * Reset all file-related state
   */
  const resetFiles = useCallback(() => {
    setFile1(null);
    setFile2(null);
    setRawData([]);
    setXAxisLabel(DEFAULT_X_LABEL);
    setYAxisLabel(DEFAULT_Y_LABEL);
    setFileError(null);
    tempFile1Data.current = null;
    tempFile2Data.current = null;
  }, []);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setFileError(null);
  }, []);

  return {
    // File states
    file1,
    file2,

    // Parsed data
    rawData,
    xAxisLabel,
    yAxisLabel,

    // Handlers
    handleFileUpload,
    resetFiles,
    removeFile1,
    removeFile2,

    // Status
    isFileReady: file1 !== null && file2 !== null && rawData.length > 0,
    fileError,
    clearError
  };
};

export default useDataCloudFile;
